"use client";

import React, { useState } from 'react';
import { X } from 'lucide-react';
import TurnstileWidget, { useTurnstile } from '@/components/TurnstileWidget';
import { analytics } from '@/lib/analytics/events';
import { getStoredAttribution } from '@/lib/analytics/attribution';
import {
  classifyPhoneInput,
  formatPhoneAsTyped,
  phoneMaskNotice,
  PHONE_HINT_TEXT,
  PHONE_PLACEHOLDER,
} from '@/lib/phone';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  builderName: string;
  builderWebsite: string;
  contactName: string;
  contactTitle: string;
  address: string;
  phone: string;
  email: string;
  builderType: string;
  avgSalesPrice: string;
  numberOfUnits: string;
  state: string;
  numberOfEmployees: string;
  optIn: boolean;
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const ConsultationModal: React.FC<ConsultationModalProps> = ({ isOpen, onClose }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionData, setSubmissionData] = useState<{ requestId?: string; message?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Submission-level error, rendered inline above the form actions.
  const [submitError, setSubmitError] = useState('');
  // Honeypot — a hidden field bots fill and humans never see.
  const [honeypot, setHoneypot] = useState('');
  // Bot check (Cloudflare Turnstile) — token/reset lifecycle lives in the
  // shared hook; the token is verified server-side before the DB write and
  // CRM sync run.
  const turnstile = useTurnstile('consultation');
  const [formData, setFormData] = useState<FormData>({
    builderName: '',
    builderWebsite: '',
    contactName: '',
    contactTitle: '',
    address: '',
    phone: '',
    email: '',
    builderType: '',
    avgSalesPrice: '',
    numberOfUnits: '',
    state: '',
    numberOfEmployees: '',
    optIn: false,
  });

  interface FormErrors {
    builderName?: string;
    builderWebsite?: string;
    contactName?: string;
    contactTitle?: string;
    address?: string;
    phone?: string;
    email?: string;
    builderType?: string;
    avgSalesPrice?: string;
    numberOfUnits?: string;
    state?: string;
    numberOfEmployees?: string;
    optIn?: string;
  }

  const [errors, setErrors] = useState<FormErrors>({});
  // Advisory, not a refusal: a pasted extension, which we can't store. The
  // mask leaves such a value alone, so this is what names the problem before
  // the submit-time refusal does. Muted, never blocks submit, recomputed on
  // every edit.
  const [phoneNotice, setPhoneNotice] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    let processedValue = value;

    // Phone masks as it's typed. The mask lives in the shared util so this
    // modal, the assessment form and the admin/portal editors group digits
    // identically, and it formats without ever dropping a digit — past a US
    // shape it hands back bare digits for validation to refuse. A pasted
    // extension gets the advisory notice below the field.
    if (name === 'phone' && type !== 'checkbox') {
      processedValue = formatPhoneAsTyped(value);
      setPhoneNotice(phoneMaskNotice(value));
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : processedValue
    }));

    // Clear errors when user starts typing — both the per-field message and
    // any submission-level error from the previous attempt.
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.builderName.trim()) newErrors.builderName = 'Builder Name is required';
    if (!formData.contactName.trim()) newErrors.contactName = 'Contact Name is required';
    if (!formData.contactTitle.trim()) newErrors.contactTitle = 'Contact Title is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.builderType) newErrors.builderType = 'Builder Type is required';
    if (!formData.avgSalesPrice) newErrors.avgSalesPrice = 'Average Sales Price is required';
    if (!formData.numberOfUnits) newErrors.numberOfUnits = 'Number of Units is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.numberOfEmployees) newErrors.numberOfEmployees = 'Number of Employees is required';
    if (!formData.optIn) newErrors.optIn = 'You must agree to receive communications';
    
    // Builder Website validation
    if (!formData.builderWebsite.trim()) {
      newErrors.builderWebsite = 'Builder Website is required';
    } else {
      // Check if it's a valid URL format
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
      const hasProtocol = /^https?:\/\//i.test(formData.builderWebsite);
      const testUrl = hasProtocol ? formData.builderWebsite : `https://${formData.builderWebsite}`;
      
      if (!urlPattern.test(testUrl)) {
        newErrors.builderWebsite = 'Please enter a valid website URL (e.g., example.com or https://example.com)';
      }
    }
    
    // Phone validation — deferred to the shared util so the client says
    // exactly what the server will accept (the old digit-count check passed
    // 11 digits that don't start with 1, which the server refuses) and names
    // the same specific problem the route's 400 would.
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const problem = classifyPhoneInput(formData.phone);
      if (problem) newErrors.phone = problem.message;
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailPattern.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/consultation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          website: honeypot,
          turnstileToken: turnstile.token ?? undefined,
          attribution: getStoredAttribution() ?? undefined,
        }),
      });

      const result: { requestId?: string; message?: string; error?: string } =
        await response.json().catch(() => ({}));

      if (response.ok) {
        analytics.consultationRequested();
        setSubmissionData({
          requestId: result.requestId,
          message: result.message || 'Thank you! We will contact you soon to schedule your consultation.'
        });
        setIsSubmitted(true);

        // Reset form
        setFormData({
          builderName: '',
          builderWebsite: '',
          contactName: '',
          contactTitle: '',
          address: '',
          phone: '',
          email: '',
          builderType: '',
          avgSalesPrice: '',
          numberOfUnits: '',
          state: '',
          numberOfEmployees: '',
          optIn: false,
        });
        setHoneypot('');
        setPhoneNotice(null);
      } else {
        setSubmitError(result.error || 'Failed to submit consultation request. Please try again.');
        // 400/429 are issued before the server runs verification, so the
        // token is still valid; anything else consumed it — mint a fresh one.
        if (response.status !== 400 && response.status !== 429) {
          turnstile.discardToken();
        }
      }
    } catch (error) {
      // The request never reached the server; the token is still valid.
      console.error('Error submitting form:', error);
      setSubmitError('Failed to submit consultation request. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setSubmissionData({});
    setSubmitError('');
    // The instance outlives the closed modal, so a notice about a paste from
    // last time would otherwise greet the next visitor.
    setPhoneNotice(null);
    // The modal instance (and its token state) outlives the closed modal,
    // but the widget unmounts with it — by reopen time a held token would be
    // expired or already consumed, so drop it now and re-challenge on open.
    turnstile.discardToken();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl border border-gray-200">
        <div className="overflow-y-auto min-h-0 p-4 sm:p-6" style={{ maxHeight: 'calc(90vh - 2rem)' }}>
          {isSubmitted ? (
            // Thank You Content
            <div className="text-center">
              <div className="flex justify-end mb-4">
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="flex flex-col justify-center h-full py-8">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
                <p className="text-base sm:text-lg text-gray-700 mb-8 max-w-md mx-auto">
                  {submissionData.message}
                </p>
                
                <button
                  onClick={handleClose}
                  className="mx-auto px-6 py-2 text-sm text-white rounded-md hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#0B253C' }}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            // Form Content
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Schedule Your Consultation</h2>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </>
          )}
          
          {!isSubmitted && (
            <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Builder Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Builder Name *
                </label>
                <input
                  type="text"
                  name="builderName"
                  value={formData.builderName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.builderName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.builderName && <p className="text-red-500 text-xs mt-1">{errors.builderName}</p>}
              </div>

              {/* Builder Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Builder Website *
                </label>
                <input
                  type="text"
                  name="builderWebsite"
                  value={formData.builderWebsite}
                  onChange={handleInputChange}
                  placeholder="example.com"
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.builderWebsite ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.builderWebsite && <p className="text-red-500 text-xs mt-1">{errors.builderWebsite}</p>}
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name *
                </label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.contactName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.contactName && <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>}
              </div>

              {/* Contact Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Title *
                </label>
                <input
                  type="text"
                  name="contactTitle"
                  value={formData.contactTitle}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.contactTitle ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.contactTitle && <p className="text-red-500 text-xs mt-1">{errors.contactTitle}</p>}
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.address ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={PHONE_PLACEHOLDER}
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  aria-describedby={
                    phoneNotice && !errors.phone
                      ? 'consultation-phone-notice consultation-phone-hint'
                      : 'consultation-phone-hint'
                  }
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                {/* Advisory, so muted rather than the red error
                    treatment. Hidden once the refusal is showing: both carry
                    the same extension sentence. */}
                {phoneNotice && !errors.phone && (
                  <p
                    id="consultation-phone-notice"
                    role="status"
                    className="text-gray-500 text-xs mt-1"
                  >
                    {phoneNotice}
                  </p>
                )}
                {/* Same hint copy as every other phone field in the app. */}
                <p id="consultation-phone-hint" className="text-gray-500 text-xs mt-1">
                  {PHONE_HINT_TEXT}
                </p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Builder Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Builder Type *
                </label>
                <select
                  name="builderType"
                  value={formData.builderType}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.builderType ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Builder Type</option>
                  <option value="production">Production</option>
                  <option value="custom">Custom</option>
                  <option value="hybrid">Hybrid (little of both)</option>
                </select>
                {errors.builderType && <p className="text-red-500 text-xs mt-1">{errors.builderType}</p>}
              </div>

              {/* Average Sales Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Average Sales Price *
                </label>
                <select
                  name="avgSalesPrice"
                  value={formData.avgSalesPrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.avgSalesPrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Price Range</option>
                  <option value="0-500k">$0 - $500K</option>
                  <option value="501-700k">$501K - $700K</option>
                  <option value="701k-1m">$701K - $1M</option>
                  <option value="1m+">$1M+</option>
                </select>
                {errors.avgSalesPrice && <p className="text-red-500 text-xs mt-1">{errors.avgSalesPrice}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Number of Units */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Units *
                </label>
                <select
                  name="numberOfUnits"
                  value={formData.numberOfUnits}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.numberOfUnits ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Number of Units</option>
                  <option value="0-5">0-5</option>
                  <option value="6-25">6-25</option>
                  <option value="26-50">26-50</option>
                  <option value="51-100">51-100</option>
                  <option value="100+">100+</option>
                </select>
                {errors.numberOfUnits && <p className="text-red-500 text-xs mt-1">{errors.numberOfUnits}</p>}
              </div>

              {/* Number of Employees */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Employees *
                </label>
                <select
                  name="numberOfEmployees"
                  value={formData.numberOfEmployees}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.numberOfEmployees ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Number of Employees</option>
                  <option value="0-10">0-10</option>
                  <option value="11-25">11-25</option>
                  <option value="26-50">26-50</option>
                  <option value="50+">50+</option>
                </select>
                {errors.numberOfEmployees && <p className="text-red-500 text-xs mt-1">{errors.numberOfEmployees}</p>}
              </div>
            </div>

            {/* State Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State Building In *
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.state ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select State</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
            </div>

            {/* Opt-in Checkbox */}
            <div className="border-t pt-3">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="optIn"
                  checked={formData.optIn}
                  onChange={handleInputChange}
                  className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-xs sm:text-sm text-gray-700">
                  <span className="font-medium">I agree to receive communications *</span>
                  <br />
                  <span className="text-gray-600">
                    By checking this box, I consent to receive marketing communications from Builder Collaborative 
                    including emails, phone calls, and text messages. I understand I can opt out at any time.
                  </span>
                </label>
              </div>
              {errors.optIn && <p className="text-red-500 text-xs mt-1">{errors.optIn}</p>}
            </div>

            <TurnstileWidget {...turnstile.widgetProps} />

            {submitError && (
              <p role="alert" className="text-red-600 text-sm">{submitError}</p>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || turnstile.awaiting}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#0B253C' }}
              >
                {isSubmitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>

            {/* Honeypot: hidden from humans; bots fill it and get silently
                dropped server-side. Kept as the form's LAST child so the
                space-y-3 sibling selector doesn't add a phantom gap above
                the first visible row. */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="pointer-events-none absolute left-[-9999px] h-0 w-0 overflow-hidden opacity-0"
            />
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultationModal;
